import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";

const SESSION_TONE = {
  none: "text-slate-400", current: "text-emerald-600", new_documents_available: "text-amber-600",
};
const SESSION_LABEL = {
  none: "No session yet", current: "Current", new_documents_available: "New documents available",
};

function Stat({ value, label, tone, dot }) {
  return (
    <div className="flex items-center gap-2" data-testid={`dochealth-${label.toLowerCase().replace(/ /g, "-")}`}>
      {dot && <span className={`h-2 w-2 rounded-full ${dot}`} />}
      <span className={`text-lg font-semibold ${tone || "text-slate-800"}`}>{value}</span>
      <span className="text-[11px] text-slate-400 uppercase tracking-wide">{label}</span>
    </div>
  );
}

/** Phase 2D closeout — one-glance Document Health for the client's package. */
export default function DocumentHealthStrip({ clientId }) {
  const [h, setH] = useState(null);
  useEffect(() => {
    api.get(`/documents-intel/package/${clientId}/health`).then((r) => setH(r.data)).catch(() => {});
  }, [clientId]);
  if (!h) return null;

  const allUnderstood = h.total > 0 && h.understood === h.total && h.need_classification === 0 && h.needs_review === 0;
  const dot = h.total === 0 ? "bg-slate-300" : allUnderstood ? "bg-emerald-500" : (h.need_classification || h.needs_review) ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4" data-testid="document-health-strip">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Icons.HeartPulse className="h-4 w-4 text-slate-500" />
          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Document Health</span>
        </div>
        <div className="flex items-center gap-5 flex-wrap">
          <Stat value={h.total} label="Documents" dot={dot} />
          <Stat value={h.understood} label="Understood" tone="text-emerald-600" />
          {h.need_classification > 0 && <Stat value={h.need_classification} label="Need Classification" tone="text-amber-600" />}
          {h.needs_review > 0 && <Stat value={h.needs_review} label="Needs Review" tone="text-rose-600" />}
          <div className="flex items-center gap-1.5 pl-4 border-l border-slate-100" data-testid="dochealth-session">
            <Icons.Sparkles className="h-3.5 w-3.5 text-slate-300" />
            <div className="leading-tight">
              <div className="text-[10px] text-slate-400 uppercase tracking-wide">Intelligence Session</div>
              <div className={`text-[12px] font-medium ${SESSION_TONE[h.session_status] || "text-slate-500"}`}>
                {SESSION_LABEL[h.session_status] || h.session_status}
                {h.last_session_at ? ` · ${new Date(h.last_session_at).toLocaleDateString()}` : ""}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
